/**
 * Plausible Analytics — lightweight, privacy-friendly analytics.
 *
 * Injects the Plausible script tag into the document head.
 * No-ops when domain is not configured or when running in a non-browser context.
 *
 * Usage in main.tsx:
 *   initPlausible({ domain: import.meta.env.VITE_PLAUSIBLE_DOMAIN });
 */

let initialized = false;

export interface PlausibleConfig {
  /** The site domain registered in Plausible (e.g. "app.peekbox.io") */
  domain?: string;
  /** Custom Plausible API host. Defaults to "https://plausible.io" */
  apiHost?: string;
}

/**
 * Inject the Plausible `<script>` tag into the document head.
 * Safe to call multiple times (idempotent).
 */
export function initPlausible(config: PlausibleConfig): void {
  if (initialized || !config.domain) return;
  if (typeof document === 'undefined') return; // SSR guard

  const apiHost = config.apiHost ?? 'https://plausible.io';
  const script = document.createElement('script');
  script.defer = true;
  script.dataset.domain = config.domain;
  script.dataset.api = `${apiHost}/api/event`;
  script.src = `${apiHost}/js/script.js`;
  document.head.appendChild(script);

  initialized = true;
}

/**
 * Track a custom event in Plausible.
 * No-ops when Plausible is not initialized or in non-browser context.
 *
 * @param eventName - Name of the event (e.g. "Box Created", "Item Added")
 * @param props - Optional custom properties to attach to the event
 */
export function trackEvent(
  eventName: string,
  props?: Record<string, string | number | boolean>,
): void {
  if (!initialized || typeof window === 'undefined') return;

  // The Plausible script exposes a global `plausible` function
  const plausible = (window as unknown as Record<string, unknown>).plausible;
  if (typeof plausible !== 'function') return;

  plausible(eventName, props ? { props } : undefined);
}
